import { useCallback, useMemo, useRef, useState } from 'react';
import {
  applyArchive,
  buildArchive,
  readArchive,
  type BuiltArchive,
  type ParsedArchive,
} from '@core/archive';
import type { Repositories } from '@core/db';
import { canShareFiles, saveFile, shareFiles, toShareFiles, type FileStore } from '@core/files';
import { archiveLine, pendingArchiveLine, restoredLine } from './formatter';

/**
 * Выгрузка и загрузка архива — шаг 19 [WEB_ROADMAP.md](docs/WEB_ROADMAP.md).
 *
 * **Почему выгрузка в два нажатия.** `navigator.share` на iOS открывается только
 * из обработчика жеста: всё, что случилось после `await`, Safari считает не действием
 * человека и отклоняет молча (`share.ts`). Собрать архив мгновенно нельзя — это сотни
 * мегабайт документов. Поэтому сначала «Подготовить архив», а потом, когда он уже
 * в руках, «Поделиться» — и лист открывается сразу по нажатию.
 */

export interface ArchiveState {
  /** Идёт ли сейчас долгая работа — сборка, чтение или загрузка. */
  busy: 'building' | 'reading' | 'applying' | null;
  /** Готовый архив: «3 дела, 12 документов · 12,4 МБ». */
  ready: string | null;
  /** Документы, у которых не нашлось файла: архив собран, но не полон. */
  incomplete: string | null;
  /** Что внутри выбранного архива — показывается до загрузки. */
  pending: string | null;
  message: string | null;
  error: string | null;
}

const reason = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : 'Не удалось прочитать архив';

export function useArchive(db: Repositories, files: FileStore, version: string, onDone: () => void) {
  const built = useRef<BuiltArchive | null>(null);
  const parsed = useRef<ParsedArchive | null>(null);

  const [busy, setBusy] = useState<ArchiveState['busy']>(null);
  const [ready, setReady] = useState<string | null>(null);
  const [incomplete, setIncomplete] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const state: ArchiveState = useMemo(
    () => ({ busy, ready, incomplete, pending, message, error }),
    [busy, ready, incomplete, pending, message, error],
  );

  const prepare = useCallback(async () => {
    setBusy('building');
    setError(null);

    try {
      const archive = await buildArchive(db, files, version);
      built.current = archive;

      setReady(archiveLine(archive.contents, archive.blob.size));
      setIncomplete(
        archive.missing.length > 0
          ? `Не нашлось файлов: ${archive.missing.length}. Остальное в архиве`
          : null,
      );
    } catch (failure) {
      setError(reason(failure));
    } finally {
      setBusy(null);
    }
  }, [db, files, version]);

  const actions = useMemo(
    () => ({
      prepare,

      /**
       * Отдаёт готовый архив. Вызывается **непосредственно из нажатия**: ничего
       * долгого до `navigator.share` здесь произойти не должно.
       */
      send() {
        const archive = built.current;
        if (!archive) return;

        const shared = toShareFiles([
          {
            fileName: archive.fileName,
            mimeType: 'application/zip',
            content: archive.blob,
          },
        ]);

        // Где системного листа нет — обычная загрузка файла: выгрузка не имеет права
        // зависеть от одного способа.
        if (!canShareFiles(shared)) {
          saveFile(archive.blob, archive.fileName);
          return;
        }
        void shareFiles(shared, 'Архив дел Yatwa');
      },

      /** Разбирает выбранный файл и показывает, что внутри. Загрузки ещё нет. */
      async pick(chosen: File[]) {
        const file = chosen[0];
        if (!file) return;

        setBusy('reading');
        setError(null);

        try {
          const archive = await readArchive(file);
          parsed.current = archive;
          setPending(pendingArchiveLine(archive.manifest.contents, archive.manifest.createdAt));
        } catch (failure) {
          parsed.current = null;
          setError(reason(failure));
        } finally {
          setBusy(null);
        }
      },

      /** Разворачивает разобранный архив на устройстве. */
      async confirm() {
        const archive = parsed.current;
        if (!archive) return;

        setBusy('applying');
        setPending(null);

        try {
          const summary = await applyArchive(db, files, archive);
          setMessage(restoredLine(summary));
          onDone();
        } catch (failure) {
          setError(reason(failure));
        } finally {
          parsed.current = null;
          setBusy(null);
        }
      },

      cancel() {
        parsed.current = null;
        setPending(null);
      },

      shown() {
        setMessage(null);
        setError(null);
      },
    }),
    [db, files, onDone, prepare],
  );

  return { state, actions };
}

export type ArchiveActions = ReturnType<typeof useArchive>['actions'];
